import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useDaggerheartData } from '../context/DataContext';

/**
 * LootCollectionManager Component
 * Simple loot rolling experience similar to bloom collection.
 * Choose rarity, roll dice, get loot based on the roll result.
 *
 * @param {object} props - The component props.
 * @param {object} props.character - The current character data object.
 * @param {function} props.updateCharacter - Function to update the character state.
 * @param {function} props.onManagerClose - Callback to close the modal.
 */
const LootCollectionManager = ({ character, updateCharacter, onManagerClose }) => {
  const allData = useDaggerheartData();
  
  // Early return if loot data is not loaded
  if (!allData || !allData.loot) {
    return <div>Loading loot data...</div>;
  }

  const [selectedRarity, setSelectedRarity] = useState('common');
  const [rollResult, setRollResult] = useState(null);
  const [collectedLoot, setCollectedLoot] = useState([]);
  const [isRolling, setIsRolling] = useState(false);

  const { items, consumables, rarityRolls } = allData.loot;

  // Rarity configurations with dice and descriptions
  const rarityConfigs = {
    common: {
      name: 'Common',
      dice: '1d12 or 2d12',
      color: 'bg-gray-200 text-gray-800',
      description: 'Found at abandoned camps or local shops'
    },
    uncommon: {
      name: 'Uncommon', 
      dice: '2d12 or 3d12',
      color: 'bg-green-200 text-green-800',
      description: 'Limited shop supply or job rewards'
    },
    rare: {
      name: 'Rare',
      dice: '3d12 or 4d12', 
      color: 'bg-blue-200 text-blue-800',
      description: 'Locked away treasures or sole job rewards'
    },
    legendary: {
      name: 'Legendary',
      dice: '4d12 or 5d12',
      color: 'bg-purple-200 text-purple-800',
      description: 'One-of-a-kind, incredibly dangerous rewards'
    }
  };

  const handleLootRoll = () => {
    try {
      setIsRolling(true);
      
      // Determine number of dice (1-2 for common, 2-3 for uncommon, etc.)
      const diceCount = selectedRarity === 'common' ? Math.floor(Math.random() * 2) + 1 :
                       selectedRarity === 'uncommon' ? Math.floor(Math.random() * 2) + 2 :
                       selectedRarity === 'rare' ? Math.floor(Math.random() * 2) + 3 :
                       Math.floor(Math.random() * 2) + 4; // legendary
      
      // Roll the dice
      let totalRoll = 0;
      const individualRolls = [];
      for (let i = 0; i < diceCount; i++) {
        const roll = Math.ceil(Math.random() * 12);
        individualRolls.push(roll);
        totalRoll += roll;
      }
      
      // Randomly choose between items and consumables
      const isConsumable = Math.random() < 0.5;
      const targetArray = isConsumable ? consumables : items;
      
      // Filter by rarity and find items within roll range
      const possibleLoot = targetArray.filter(loot => loot.rarity === selectedRarity);
      
      if (possibleLoot.length === 0) {
        toast.success(`No ${selectedRarity} loot available!`);
        setIsRolling(false);
        return;
      }
      
      // For simplicity, map roll ranges to item positions
      // Higher rolls get items later in the list (typically better within same rarity)
      const maxRoll = diceCount * 12;
      const rollPercentage = totalRoll / maxRoll;
      const itemIndex = Math.floor(rollPercentage * possibleLoot.length);
      const selectedLoot = possibleLoot[Math.min(itemIndex, possibleLoot.length - 1)];
      
      const result = {
        loot: selectedLoot,
        roll: totalRoll,
        individualRolls,
        diceUsed: `${diceCount}d12`,
        rarity: selectedRarity,
        isConsumable
      };
      
      console.log('Loot roll result:', result);
      setRollResult(result);
      setCollectedLoot([selectedLoot]);
      setIsRolling(false);
      
    } catch (error) {
      console.error('Error in handleLootRoll:', error);
      toast.success(`Error during loot roll: ${error.message}`);
      setIsRolling(false);
    }
  };

  const handleAddToInventory = () => {
    if (collectedLoot.length === 0) return;
    
    const lootItem = collectedLoot[0];
    const currentItems = character.inventory?.items || [];
    const existingItem = currentItems.find(item => item.id === lootItem.id);
    
    let updatedItems;
    if (existingItem && lootItem.type === 'consumable') {
      // Increase quantity for consumables (max 5)
      const maxQuantity = 5;
      const newQuantity = Math.min((existingItem.quantity || 1) + 1, maxQuantity);
      
      if (newQuantity > (existingItem.quantity || 1)) {
        updatedItems = currentItems.map(item => 
          item.id === lootItem.id 
            ? { ...item, quantity: newQuantity }
            : item
        );
      } else {
        toast.success('You can only carry 5 of each consumable at a time!');
        return;
      }
    } else {
      // Add new item
      const newItem = {
        ...lootItem,
        id: lootItem.type === 'consumable' ? lootItem.id : `${lootItem.id}-${Date.now()}`,
        quantity: lootItem.type === 'consumable' ? 1 : 1,
        source: 'loot'
      };
      updatedItems = [...currentItems, newItem];
    }

    updateCharacter({
      inventory: {
        ...character.inventory,
        items: updatedItems
      }
    });

    setCollectedLoot([]);
    setRollResult(null);
    toast.success(`Added ${lootItem.name} to inventory!`);
  };

  const getLootTypeIcon = (type) => {
    switch(type) {
      case 'consumable': return '🧪';
      case 'recipe': return '📜';
      case 'relic': return '💎';
      case 'gem': return '💍';
      case 'item':
      default: return '📦';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-bold text-amber-600 mb-2">🏺 Discover Loot</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Roll dice to discover items and consumables based on rarity
        </p>
      </div>

      {/* Rarity Selection */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <h4 className="font-semibold mb-3">Choose Rarity</h4>
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(rarityConfigs).map(([rarity, config]) => (
            <button
              key={rarity}
              onClick={() => setSelectedRarity(rarity)}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                selectedRarity === rarity 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-300 dark:border-gray-600 hover:border-blue-300'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold">{config.name}</span>
                <span className={`px-2 py-1 text-xs rounded ${config.color}`}>
                  {config.dice}
                </span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {config.description}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Roll Button */}
      <div className="text-center">
        <button 
          onClick={handleLootRoll}
          disabled={isRolling}
          className="px-8 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium text-lg"
        >
          {isRolling ? 'Rolling...' : `🎲 Roll for ${rarityConfigs[selectedRarity].name} Loot`}
        </button>
      </div>

      {/* Roll Result */}
      {rollResult && (
        <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-amber-800 dark:text-amber-200">
              🎲 Roll Result
            </h4>
            <div className="text-sm text-amber-700 dark:text-amber-300">
              <span className="font-mono">{rollResult.roll}</span> 
              <span className="ml-2 text-xs">({rollResult.diceUsed}: {rollResult.individualRolls.join(', ')})</span>
            </div>
          </div>
          
          {collectedLoot.length > 0 && (
            <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">{getLootTypeIcon(collectedLoot[0].type)}</span>
                  <div>
                    <h5 className="font-bold text-lg">{collectedLoot[0].name}</h5>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 text-xs rounded capitalize font-medium ${rarityConfigs[collectedLoot[0].rarity].color}`}>
                        {collectedLoot[0].rarity}
                      </span>
                      <span className="text-xs text-gray-500 capitalize">
                        {collectedLoot[0].type}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {collectedLoot[0].description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <button 
            onClick={handleAddToInventory}
            className="w-full mt-4 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 font-medium"
          >
            Add to Inventory
          </button>
        </div>
      )}

      {/* Close Button */}
      <div className="flex justify-end pt-4">
        <button 
          onClick={onManagerClose}
          className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
        >
          Close
        </button>
      </div>

      {/* Current Loot Items in Inventory */}
      {character.inventory?.items && character.inventory.items.filter(item => item.source === 'loot').length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
          <h4 className="font-semibold mb-3">
            Current Loot ({character.inventory.items.filter(item => item.source === 'loot').length} items)
          </h4>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {character.inventory.items
              .filter(item => item.source === 'loot')
              .map(item => (
                <div key={item.id} className="flex items-center justify-between p-2 bg-white dark:bg-gray-700 rounded-md text-sm">
                  <div className="flex items-center space-x-2">
                    <span>{getLootTypeIcon(item.type)}</span>
                    <span className="font-medium">{item.name}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    {item.quantity > 1 && <span>×{item.quantity}</span>}
                    <span className={`px-1 py-0.5 rounded capitalize ${rarityConfigs[item.rarity]?.color || 'bg-gray-200 text-gray-800'}`}>
                      {item.rarity}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LootCollectionManager;
